import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import {
  useQueryStates,
  parseAsInteger,
  parseAsArrayOf,
  parseAsStringLiteral,
} from 'nuqs'
import { useInvoiceConfigsQuery } from 'queries/invoiceConfig/useInvoiceConfigsQuery'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { BREAKDOWN_LEVEL_OPTIONS } from '../schemas/invoiceConfigValues'

type BreakdownLevel = (typeof BREAKDOWN_LEVEL_OPTIONS)[number]['value']

const BREAKDOWN_LEVEL_VALUES = BREAKDOWN_LEVEL_OPTIONS.map(
  (option) => option.value
) as [BreakdownLevel, ...BreakdownLevel[]]

type InvoiceConfigContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useInvoiceConfigsQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    companyId: number | null
    breakdownLevels: BreakdownLevel[]
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setCompanyId: (value: number | undefined) => void
    setBreakdownLevels: (value: BreakdownLevel[]) => void
  }
}

const [Context, useInvoiceConfigContextValue] =
  createContext<InvoiceConfigContextState>({
    name: 'InvoiceConfigContext',
  })

export const InvoiceConfigProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [pagination, setPagination] = usePaginationQueryState()
  const [filters, setFilters] = useQueryStates(
    {
      companyId: parseAsInteger,
      breakdownLevels: parseAsArrayOf(
        parseAsStringLiteral(BREAKDOWN_LEVEL_VALUES)
      ).withDefault([]),
    },
    {
      urlKeys: {
        companyId: 'company',
        breakdownLevels: 'breakdownLevels',
      },
    }
  )

  const setCompanyId = (value: number | undefined) => {
    setFilters({ companyId: value ?? null })
    setPagination({ pageIndex: 0 })
  }

  const setBreakdownLevels = (value: BreakdownLevel[]) => {
    setFilters({ breakdownLevels: value })
    setPagination({ pageIndex: 0 })
  }

  const {
    data,
    isPending: isInvoiceConfigsLoading,
    isError: isInvoiceConfigsError,
    error: invoiceConfigsError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useInvoiceConfigsQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    companyId: filters.companyId ?? undefined,
    breakdownLevels:
      filters.breakdownLevels.length > 0 ? filters.breakdownLevels : undefined,
  })

  if (isInvoiceConfigsLoading) {
    return <Loading />
  }

  if (isInvoiceConfigsError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError: isInvoiceConfigsError,
      error: parseErrorMessage(invoiceConfigsError),
      companyId: filters.companyId,
      breakdownLevels: filters.breakdownLevels,
      pagination,
    },
    actions: {
      refetch,
      setPagination,
      setCompanyId,
      setBreakdownLevels,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useInvoiceConfigContextValue }
